import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, capitalize, IconButton, Tooltip, Typography, useMediaQuery, useTheme, Alert } from "@mui/material";
import { makeStyles } from "@mui/styles";
import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import { ArchiveFile } from "v2-types/order";
import { convertToBytes, convertUnixTimestamp } from "views/orders/documents/new_documents/utils";
import { PartiesData } from "./PartiesData";

const useStyles = makeStyles((theme: any) => ({
  alert: {
    marginTop: "1rem", paddingTop: "0px", paddingBottom: "0px"
  },
}));

type Props = {
  file: ArchiveFile,
  onRequestSignature: (fileSource: string) => void,
  handleDownload: (link: string, fileName: string) => void,
  viewFile: (fileId: string) => void,
  showBorderBottom: boolean
}

export const FileData: FC<Props> = ({
  file: { data, mtime, size, _id, download, filename, sign_check, source, sign_error },
  onRequestSignature,
  handleDownload,
  viewFile,
  showBorderBottom
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const fileContainsSigners = (data || []).filter(({ kind }) => kind === "signer").length > 0;
  return (
    <>
      {sign_error && fileContainsSigners && (
      <Alert severity="error" color="error" className={classes.alert}>{sign_error}</Alert>
      )}
      <Box style={{
        display: "flex",
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: "space-between",
        borderBottom: showBorderBottom ? "1px solid lightgray" : "none",
        padding: theme.spacing(2, 0),
        gap: isMobile ? theme.spacing(1) : 0,
      }}
      >
        <Box
          width={isMobile ? '100%' : '50%'}
          display="grid"
          gridTemplateColumns="minmax(0, 1fr)"
          gap={theme.spacing(1)}
          columnGap={theme.spacing(1)}
          style={{ minWidth: 0 }}
        >
          {data && <PartiesData data={data} />}
        </Box>
        <Box
          style={{
            display: "flex",
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? theme.spacing(1) : theme.spacing(6),
            alignItems: isMobile ? 'flex-start' : 'center',
            width: isMobile ? '100%' : 'auto'
          }}
        >
          <Box
            display="flex"
            gap={theme.spacing(1)}
            style={{
              alignItems: "center",
              justifyContent: isMobile ? 'space-between' : 'flex-start',
              width: isMobile ? '100%' : 'auto'
            }}
          >
            <Typography variant="body2">{capitalize(convertUnixTimestamp(mtime))}</Typography>
            <Typography variant="body2" style={{ fontFamily: 'monospace', textAlign: 'right', minWidth: '6rem' }}>{convertToBytes(size)}</Typography>
          </Box>

          <Box style={{ display: "flex", gap: theme.spacing(0.5), alignItems: "center", marginLeft: isMobile ? 'auto' : 0 }}>
            {sign_check && (
            <Tooltip title={t('documents:requestSignature')}>
              <IconButton
                size="small"
                style={{ padding: isMobile ? theme.spacing(1.5) : undefined }}
                onClick={() => onRequestSignature(source)}
              >
                <FontAwesomeIcon icon="file-signature" />
              </IconButton>
            </Tooltip>
            )}
            {!sign_check && (
            <IconButton size="small" disabled style={{ padding: theme.spacing(1.5), visibility: 'hidden' }}>
              <FontAwesomeIcon icon="file-signature" />
            </IconButton>
            )}

            <Tooltip title={t('documents:viewFile')}>
              <IconButton
                size="small"
                style={{ padding: isMobile ? theme.spacing(1.5) : undefined }}
                onClick={() => viewFile(_id)}
              >
                <FontAwesomeIcon icon="eye" />
              </IconButton>
            </Tooltip>

            <Tooltip title={t('documents:download')}>
              <IconButton
                size="small"
                style={{ padding: isMobile ? theme.spacing(1.5) : undefined }}
                onClick={() => handleDownload(download, filename)}
              >
                <FontAwesomeIcon icon="download" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>
    </>
  );
};
